import React, { useState, useEffect } from 'react';
import { useVitalTests } from '../../hooks/useVitalTests';
import { FaTimes } from 'react-icons/fa';

const VitalTestModal = () => {
  const { 
    isModalOpen, 
    closeModal, 
    createVitalTest, 
    updateVitalTest, 
    selectedVitalTest,
    modalMode
  } = useVitalTests();

  const initialFormState = {
    name: '',
    test_code: '',
    test_type: 'TEST',
    description: '',
    sale_price: '',
    has_fasting: 'NO',
    has_disposal: 'NO',
    preparation: '',
    sub_category_id: '',
    home_sub_category_id: '',
    home_collection_available: false,
    is_blood_test: false,
    child_test_details: null,
    child_test_home_sub_category_id: null,
    child_test_center_sub_category_id: null,
    is_active: true
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or selected test changes
  useEffect(() => {
    if (isModalOpen) {
      if (modalMode === 'edit' && selectedVitalTest) {
        setFormData({
          ...initialFormState,
          ...selectedVitalTest
        });
      } else {
        setFormData(initialFormState);
      }
      setErrors({});
    }
  }, [isModalOpen, selectedVitalTest, modalMode]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Test name is required';
    if (!formData.test_code) newErrors.test_code = 'Test code is required';
    if (!formData.sale_price) newErrors.sale_price = 'Sale price is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle different input types
    const inputValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: inputValue
    }));
    
    // Clear error for this field if any
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      if (modalMode === 'create') {
        await createVitalTest(formData);
      } else {
        const testId = selectedVitalTest._id || selectedVitalTest.id;
        await updateVitalTest(testId, formData);
      }
      closeModal();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">
            {modalMode === 'create' ? 'Add New Vital Test' : 'Edit Vital Test'}
          </h2>
          <button 
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className="overflow-y-auto p-4 max-h-[calc(90vh-120px)]">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Test Name */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                  placeholder="Enter test name"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Test Code */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="test_code"
                  value={formData.test_code || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.test_code ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                  placeholder="Enter test code"
                />
                {errors.test_code && <p className="text-red-500 text-xs mt-1">{errors.test_code}</p>}
              </div>

              {/* Test Type */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Type
                </label>
                <select
                  name="test_type"
                  value={formData.test_type || 'TEST'}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="TEST">TEST</option>
                  <option value="PACKAGE">PACKAGE</option>
                </select>
              </div>

              {/* Sale Price */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sale Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="sale_price"
                  value={formData.sale_price || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.sale_price ? 'border-red-500' : 'border-gray-300'} rounded-md`}
                  placeholder="Enter sale price"
                />
                {errors.sale_price && <p className="text-red-500 text-xs mt-1">{errors.sale_price}</p>}
              </div>

              {/* Has Fasting */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Requires Fasting
                </label>
                <select
                  name="has_fasting"
                  value={formData.has_fasting || 'NO'}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="NO">No</option>
                  <option value="YES">Yes</option>
                </select>
              </div>

              {/* Has Disposal */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Has Disposal
                </label>
                <select
                  name="has_disposal"
                  value={formData.has_disposal || 'NO'}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="NO">No</option>
                  <option value="YES">Yes</option>
                </select>
              </div>

              {/* Sub Category ID */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sub Category ID
                </label>
                <input
                  type="text"
                  name="sub_category_id"
                  value={formData.sub_category_id || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter sub category ID"
                />
              </div>

              {/* Home Sub Category ID */}
              <div className="col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Home Sub Category ID
                </label>
                <input
                  type="text"
                  name="home_sub_category_id"
                  value={formData.home_sub_category_id || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter home sub category ID"
                />
              </div>

              {/* Preparation */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preparation Instructions
                </label>
                <textarea
                  name="preparation"
                  value={formData.preparation || ''}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter preparation instructions"
                ></textarea>
              </div>

              {/* Description */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description || ''}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Enter test description"
                ></textarea>
              </div>

              {/* Checkboxes */}
              <div className="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="home_collection_available"
                    name="home_collection_available"
                    checked={formData.home_collection_available || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="home_collection_available" className="ml-2 text-sm text-gray-700">
                    Home Collection Available
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_blood_test"
                    name="is_blood_test"
                    checked={formData.is_blood_test || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="is_blood_test" className="ml-2 text-sm text-gray-700">
                    Is Blood Test
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active || false}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Is Active
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors duration-200 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Saving...' : modalMode === 'create' ? 'Create' : 'Update'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VitalTestModal;
